import React from "react";
import { Play, Pause, Square, Mic, MicOff, LayoutList } from "lucide-react";

const numberInputStyle = {
  MozAppearance: "textfield",
  WebkitAppearance: "none",
  appearance: "textfield",
};

const TeleprompterControls = ({
  teleprompter,
  recorder = null,
  isMobile = false,
  isPro = false,
  beatDuration = 0,
  song = null,
}) => {
  const hasSections = teleprompter.sections?.length > 0;

  const handleRecordToggle = () => {
    if (!recorder) return;
    if (recorder.isRecording) {
      recorder.stopRecording();
    } else {
      if (isPro && teleprompter.countdownSeconds > 0) {
        teleprompter.setShowCountdown(true);
      }
      recorder.startRecording();
      if (!teleprompter.isPlaying) {
        teleprompter.startPlayback();
      }
    }
  };

  const fontSizeOptions = [16, 20, 24, 28, 32];

  return (
    <div className="bg-forest-900/90 backdrop-blur-sm border-t border-forest-700/50 px-4 py-3">
      {/* Row 1: Playback controls */}
      <div className="flex items-center justify-center gap-3 mb-2">
        {/* Play/Pause */}
        <button
          onClick={() => {
            if (teleprompter.isPlaying) {
              teleprompter.pausePlayback();
            } else {
              teleprompter.startPlayback();
            }
          }}
          className="flex items-center justify-center w-10 h-10 rounded-full transition-colors bg-forest-700 hover:bg-forest-600 text-gold-500"
          title={teleprompter.isPlaying ? "Pause (Space)" : "Play (Space)"}
        >
          {teleprompter.isPlaying ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5 ml-0.5" />
          )}
        </button>

        {/* Stop */}
        <button
          onClick={teleprompter.stopPlayback}
          className="flex items-center justify-center w-8 h-8 rounded-full transition-colors bg-forest-700 hover:bg-forest-600 text-forest-300"
          title="Stop"
        >
          <Square className="w-4 h-4" />
        </button>

        {/* Record toggle */}
        {recorder && (
          <button
            onClick={handleRecordToggle}
            className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
              recorder.isRecording
                ? "bg-ember-500 text-white animate-pulse"
                : "bg-forest-700 hover:bg-forest-600 text-forest-300"
            }`}
            title={recorder.isRecording ? "Stop recording" : "Start recording"}
          >
            {recorder.isRecording ? (
              <MicOff className="w-5 h-5" />
            ) : (
              <Mic className="w-5 h-5" />
            )}
          </button>
        )}
      </div>

      {/* Row 2: Speed, Auto WPM, Font, Countdown, Shift */}
      <div
        className={`flex items-center justify-center gap-3 ${isMobile ? "flex-wrap" : ""}`}
      >
        {/* Speed stepper [-|###|+] — hidden when per-section speeds are set */}
        {!hasSections && (
          <>
            <div className="flex items-center" style={{ height: "28px" }}>
              <button
                onClick={() =>
                  teleprompter.setScrollSpeed((s) => Math.max(30, s - 10))
                }
                className="flex items-center justify-center rounded-l bg-forest-700 hover:bg-forest-600 text-forest-300 border border-forest-600 text-sm font-bold select-none"
                style={{ width: "28px", height: "28px" }}
                title="Decrease speed"
              >
                -
              </button>
              <input
                type="number"
                value={teleprompter.scrollSpeed}
                onChange={(e) => {
                  const v = parseInt(e.target.value);
                  if (!isNaN(v) && v >= 30 && v <= 300)
                    teleprompter.setScrollSpeed(v);
                }}
                className="text-center text-xs font-mono bg-forest-800 text-forest-200 border-y border-forest-600 focus:outline-none focus:border-gold-500"
                style={{ width: "52px", height: "28px", ...numberInputStyle }}
                min={30}
                max={300}
                step={10}
              />
              <button
                onClick={() =>
                  teleprompter.setScrollSpeed((s) => Math.min(300, s + 10))
                }
                className="flex items-center justify-center rounded-r bg-forest-700 hover:bg-forest-600 text-forest-300 border border-forest-600 text-sm font-bold select-none"
                style={{ width: "28px", height: "28px" }}
                title="Increase speed"
              >
                +
              </button>
            </div>

            {/* Auto WPM button */}
            <button
              onClick={() => teleprompter.autoDetectWPM(beatDuration)}
              disabled={beatDuration === 0}
              className={`flex items-center justify-center rounded border transition-colors text-xs font-bold select-none ${
                beatDuration === 0
                  ? "bg-forest-800 text-forest-600 border-forest-700 cursor-not-allowed"
                  : "bg-forest-700 hover:bg-forest-600 text-gold-500 border-forest-600"
              }`}
              style={{ height: "28px", padding: "0 6px" }}
              title="Auto-detect WPM from audio length"
            >
              WPM
            </button>
          </>
        )}

        {/* Sections button — only when song has audio */}
        {song?.audioFileUrl && (
          <button
            onClick={() => teleprompter.setShowSectionEditor(true)}
            className={`flex items-center gap-1 rounded border transition-colors text-xs font-bold select-none ${
              teleprompter.sections?.length > 0
                ? "bg-forest-700 hover:bg-forest-600 text-gold-500 border-gold-500/30"
                : "bg-forest-700 hover:bg-forest-600 text-forest-200 border-forest-600"
            }`}
            style={{ height: "28px", padding: "0 6px" }}
            title="Edit per-section scroll speeds"
          >
            <LayoutList className="w-3.5 h-3.5" />
            Sections{teleprompter.sections?.length > 0 ? ` (${teleprompter.sections.length})` : ""}
          </button>
        )}

        {/* Font size dropdown */}
        <select
          value={teleprompter.fontSize}
          onChange={(e) => teleprompter.setFontSize(parseInt(e.target.value))}
          className="text-xs bg-forest-700 text-forest-200 rounded px-1 border border-forest-600"
          style={{ height: "28px" }}
          title="Font size"
        >
          {fontSizeOptions.map((size) => (
            <option key={size} value={size}>{size}px</option>
          ))}
        </select>

        {/* Countdown dropdown (Pro only) */}
        {isPro && (
          <select
            value={teleprompter.countdownSeconds}
            onChange={(e) =>
              teleprompter.setCountdownSeconds(parseInt(e.target.value))
            }
            className="text-xs bg-forest-700 text-forest-200 rounded px-1 border border-forest-600"
            style={{ height: "28px" }}
            title="Countdown"
          >
            <option value={0}>Off</option>
            <option value={3}>3s</option>
            <option value={5}>5s</option>
            <option value={10}>10s</option>
          </select>
        )}

      </div>

      {/* Hide number input spinners globally via inline style tag */}
      <style>{`
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>
    </div>
  );
};

export default TeleprompterControls;
